<!-- Invoice Export Certificate Display -->
<div class="invoice-export-certificate-container">

  <!-- Invoice Header (Optional) -->
  <div *ngIf="showInvoiceHeader" class="invoice-header bg-light border-bottom p-3 mb-4">
    <div class="row align-items-center">
      <div class="col-md-6">
        <h4 class="fw-bold mb-0">Invoice: {{ invoice.invoice_number }}</h4>
      </div>
      <div class="col-md-6 text-end">
        <span class="badge badge-light-primary fs-6">
          Vehicle: {{ invoice.vehicle?.make }} {{ invoice.vehicle?.model }}
        </span>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="d-flex justify-content-center align-items-center py-10">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading certificate...</span>
    </div>
    <span class="ms-3 text-muted">Loading export certificate...</span>
  </div>

  <!-- Error State -->
  <div *ngIf="hasError && !isLoading" class="alert alert-warning d-flex align-items-center py-5" role="alert">
    <i class="ki-duotone ki-information-5 fs-2hx text-warning me-4">
      <span class="path1"></span>
      <span class="path2"></span>
      <span class="path3"></span>
    </i>
    <div class="d-flex flex-column">
      <h4 class="mb-1 text-dark">Export Certificate Not Available</h4>
      <span>{{ errorMessage }}</span>
    </div>
  </div>

  <!-- Certificate Content -->
  <div *ngIf="!isLoading && !hasError && certificate" class="certificate-content p-4 bg-white">

    <!-- Certificate Header -->
    <div class="certificate-header bg-white border-bottom mb-4">
      <div class="row align-items-center py-3">
        <div class="col-md-2">
          <div class="d-flex align-items-center">
            <span class="badge badge-secondary fs-6 fw-bold px-3 py-2">EXP. CERT.</span>
          </div>
        </div>
        <div class="col-md-8 text-center">
          <h1 class="text-danger fw-bold fs-1 mb-0" style="letter-spacing: 3px;">EXPORT CERTIFICATE</h1>
        </div>
        <div class="col-md-2 text-end">
          <div class="text-muted fs-7">
            <div class="fw-bold">Print All</div>
            <div>COMPANY_PREFIX_ID NO.</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Certificate Information Section -->
    <div class="certificate-info-section mb-4">
      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label class="form-label fw-bold">CERTIFICATE NO.</label>
            <div class="form-control-plaintext border p-2 bg-light">
              {{ certificate.certificate_no || 'N/A' }}
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label class="form-label fw-bold">REFERENCE NUMBER</label>
            <div class="form-control-plaintext border p-2 bg-light">
              {{ certificate.reference_number || 'N/A' }}
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label class="form-label fw-bold">ID NO.</label>
            <div class="form-control-plaintext border p-2 bg-light">
              {{ certificate.vehicle?.chassis_no || invoice.vehicle?.chassis_no || 'N/A' }}
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="form-group mb-3">
            <label class="form-label fw-bold">YEAR</label>
            <div class="form-control-plaintext border p-2 bg-light text-center">
              {{ certificate.vehicle?.year || invoice.vehicle?.year || getCurrentYear() }}
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="form-group mb-3">
            <label class="form-label fw-bold">MONTH</label>
            <div class="form-control-plaintext border p-2 bg-light text-center">
              {{ getCurrentMonth() }}
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="form-group mb-3">
            <label class="form-label fw-bold">DAY</label>
            <div class="form-control-plaintext border p-2 bg-light text-center">
              {{ getCurrentDay() }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Certificate Table -->
    <div class="certificate-table-container mb-4">
      <div class="table-responsive">
        <table class="table table-bordered table-sm">

          <!-- Vehicle Information Header -->
          <thead class="table-light">
            <tr>
              <th colspan="2" class="text-center fw-bold">MOTOR VEHICLE NUMBER</th>
              <th colspan="2" class="text-center fw-bold">GRANT DATE</th>
              <th colspan="2" class="text-center fw-bold">FIRST GRANT DATE</th>
              <th colspan="2" class="text-center fw-bold">CLASSIFICATION OF VEHICLE</th>
              <th class="text-center fw-bold">USE</th>
              <th colspan="3" class="text-center fw-bold">LIGHT MOTOR VEHICLE INSPECTION ORGANIZATION</th>
            </tr>
          </thead>

          <tbody>
            <!-- Vehicle Information Data -->
            <tr>
              <td colspan="2" class="text-center">{{ certificate.vehicle?.chassis_no || invoice.vehicle?.chassis_no || 'N/A' }}</td>
              <td class="text-center">YEAR</td>
              <td class="text-center">MONTH</td>
              <td class="text-center">DAY</td>
              <td class="text-center">YEAR</td>
              <td class="text-center">MONTH</td>
              <td class="text-center">DAY</td>
              <td class="text-center">{{ certificate.classification_of_vehicle || 'Private' }}</td>
              <td class="text-center">{{ certificate.purpose || 'PURPOSE' }}</td>
              <td class="text-center">{{ certificate.type_of_body || 'TYPE OF BODY' }}</td>
            </tr>

            <!-- Date Values -->
            <tr>
              <td class="text-center">{{ certificate.vehicle?.year || invoice.vehicle?.year || getCurrentYear() }}</td>
              <td class="text-center">{{ getCurrentMonth() }}</td>
              <td class="text-center">{{ getCurrentDay() }}</td>
              <td class="text-center">{{ certificate.vehicle?.year || invoice.vehicle?.year || getCurrentYear() }}</td>
              <td class="text-center">{{ getCurrentMonth() }}</td>
              <td class="text-center">{{ getCurrentDay() }}</td>
              <td class="text-center">{{ certificate.classification_of_vehicle || 'Car' }}</td>
              <td class="text-center">{{ certificate.use || 'Personal' }}</td>
              <td class="text-center">OWNERSHIP</td>
              <td class="text-center">LENGTH</td>
              <td class="text-center">WIDTH</td>
              <td class="text-center">HEIGHT</td>
            </tr>

            <!-- Maker Information Header -->
            <tr class="table-light">
              <th colspan="2" class="text-center fw-bold">MAKER'S SERIAL NUMBER</th>
              <th class="text-center fw-bold">FIXED NUMBER</th>
              <th colspan="2" class="text-center fw-bold">MAXIMUM CARGO</th>
              <th class="text-center fw-bold">WEIGHT</th>
              <th class="text-center fw-bold">OWNERSHIP</th>
              <th class="text-center fw-bold">LENGTH</th>
              <th class="text-center fw-bold">WIDTH</th>
              <th class="text-center fw-bold">HEIGHT</th>
            </tr>

            <!-- Maker Data -->
            <tr>
              <td colspan="2" class="text-center">{{ certificate.makers_serial_number || certificate.vehicle?.chassis_no || invoice.vehicle?.chassis_no || 'N/A' }}</td>
              <td class="text-center">{{ certificate.fixed_number || certificate.vehicle?.stock_no || invoice.vehicle?.stock_no || 'N/A' }}</td>
              <td class="text-center">{{ certificate.maximum_carry || 'KG' }}</td>
              <td class="text-center">KG</td>
              <td class="text-center">{{ certificate.weight || 'KG' }}</td>
              <td class="text-center">{{ certificate.g_weight || 'KG' }}</td>
              <td class="text-center">{{ certificate.length || '444' }}</td>
              <td class="text-center">{{ certificate.width || '170' }}</td>
              <td class="text-center">{{ certificate.height || '148' }}</td>
            </tr>

            <!-- Vehicle Specifications Header -->
            <tr class="table-light">
              <th class="text-center fw-bold">TRADEMARK OF THE MAKER OF MOTOR VEHICLE</th>
              <th class="text-center fw-bold">MODEL</th>
              <th class="text-center fw-bold">ENGINE MODEL</th>
              <th colspan="2" class="text-center fw-bold">CLASSIFICATION OF FUEL</th>
              <th class="text-center fw-bold">ENGINE CAPACITY</th>
              <th class="text-center fw-bold">F WEIGHT</th>
              <th class="text-center fw-bold">R WEIGHT</th>
              <th colspan="3" class="text-center fw-bold">SPECIFICATION CLASSIFICATION</th>
            </tr>

            <!-- Vehicle Specifications Data -->
            <tr>
              <td class="text-center">{{ certificate.trademark_maker || certificate.vehicle?.make || invoice.vehicle?.make || 'N/A' }}</td>
              <td class="text-center">{{ certificate.model || certificate.vehicle?.model || invoice.vehicle?.model || 'N/A' }}</td>
              <td class="text-center">{{ certificate.engine_model || certificate.vehicle?.engine || invoice.vehicle?.engine || 'N/A' }}</td>
              <td class="text-center">{{ certificate.classification_of_fuel || 'Gasoline/Hybrid' }}</td>
              <td class="text-center"></td>
              <td class="text-center">{{ certificate.engine_capacity || 'CC' }}</td>
              <td class="text-center">{{ certificate.f_weight || 'KG' }}</td>
              <td class="text-center">{{ certificate.r_weight || 'KG' }}</td>
              <td colspan="3" class="text-center">{{ certificate.specification_no || '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- User and Owner Information -->
    <div class="user-info-section mb-4">
      <div class="table-responsive">
        <table class="table table-bordered table-sm">
          <tbody>
            <!-- Type of User -->
            <tr>
              <td class="table-light fw-bold" style="width: 25%;">TYPE OF USER</td>
              <td colspan="11">{{ certificate.name_of_user || 'Imperial Solutions Ltd.' }}</td>
            </tr>

            <!-- Address of User -->
            <tr>
              <td class="table-light fw-bold">ADDRESS OF USER</td>
              <td colspan="11">{{ certificate.address_of_user || '2F-24 AOBADAI, KITAKU KOBE 651-1231, JAPAN' }}</td>
            </tr>

            <!-- Name of Owner -->
            <tr>
              <td class="table-light fw-bold">NAME OF OWNER</td>
              <td colspan="11">{{ certificate.name_of_owner || 'SAME AS USER' }}</td>
            </tr>

            <!-- Address of Owner -->
            <tr>
              <td class="table-light fw-bold">ADDRESS OF OWNER</td>
              <td colspan="11">{{ certificate.address_of_owner || 'SAME AS USER\'S ADDRESS' }}</td>
            </tr>

            <!-- Locality of Principal Place of Use -->
            <tr>
              <td class="table-light fw-bold">LOCALITY OF PRINCIPAL PLACE OF USE</td>
              <td colspan="11">{{ certificate.locality_principal_abode || 'SAME AS USER\'S ADDRESS' }}</td>
            </tr>

            <!-- Export Scheduled Day -->
            <tr>
              <td class="table-light fw-bold">EXPORT SCHEDULED DAY</td>
              <td class="text-center">YEAR</td>
              <td class="text-center">MONTH</td>
              <td class="text-center">DAY</td>
              <td colspan="8" class="text-center fw-bold">WE HEREBY CERTIFY THAT THE TRANSLATION TRUE AND CORRECT</td>
            </tr>

            <!-- Export Date Values -->
            <tr>
              <td></td>
              <td class="text-center">{{ certificate.vehicle?.year || invoice.vehicle?.year || getCurrentYear() }}</td>
              <td class="text-center">{{ getCurrentMonth() }}</td>
              <td class="text-center">{{ getCurrentDay() }}</td>
              <td colspan="8"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Company Logo and Signature Section -->
    <div class="signature-section mb-4">
      <div class="row align-items-center">
        <!-- Company Logo -->
        <div class="col-md-4">
          <div class="d-flex align-items-center">
            <div class="company-logo me-3">
              <div class="logo-circle bg-primary text-white d-flex align-items-center justify-content-center"
                   style="width: 80px; height: 80px; border-radius: 50%;">
                <span class="fw-bold fs-3">ISL</span>
              </div>
            </div>
            <div class="company-info">
              <h4 class="fw-bold mb-1">Imperial Solutions</h4>
              <p class="text-muted mb-0 fs-7">PROSPERITY FOR ALL</p>
            </div>
          </div>
        </div>

        <!-- Company Details -->
        <div class="col-md-4">
          <div class="company-details">
            <p class="fw-bold mb-1">IMPERIAL SOLUTIONS LTD.</p>
            <p class="mb-1">2F-24 AOBADAI</p>
            <p class="mb-1">KITAKU KOBE 651-1231, JAPAN</p>
            <p class="mb-0">PHONE: +81-78-201-5360</p>
          </div>
        </div>

        <!-- Signature Area -->
        <div class="col-md-4 text-end">
          <div class="signature-area">
            <div class="signature-stamp mb-3">
              <div class="stamp-placeholder border border-2 border-danger text-center p-3"
                   style="width: 120px; height: 80px; margin-left: auto;">
                <span class="text-danger fw-bold">[STAMP]</span>
              </div>
            </div>
            <div class="authorized-signature">
              <p class="fw-bold mb-1">AUTHORIZED SIGNATURE</p>
              <p class="fw-bold mb-1">Imperial Solutions</p>
              <p class="fw-bold mb-0">D.S SETHI</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Additional Certificate Details -->
    <div class="additional-details">
      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label class="form-label fw-bold">Registration No.</label>
            <div class="form-control-plaintext border p-2 bg-light">
              {{ certificate.registration_no || 'N/A' }}
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label class="form-label fw-bold">Registration Date</label>
            <div class="form-control-plaintext border p-2 bg-light">
              {{ formatDate(certificate.registration_date) || 'N/A' }}
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label class="form-label fw-bold">Final Date</label>
            <div class="form-control-plaintext border p-2 bg-light">
              {{ formatDate(certificate.final_date) || 'N/A' }}
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label class="form-label fw-bold">Vehicle</label>
            <div class="form-control-plaintext border p-2 bg-light">
              {{ invoice.vehicle?.make }} {{ invoice.vehicle?.model }} ({{ invoice.vehicle?.year }})
            </div>
          </div>
        </div>
      </div>

      <div class="row" *ngIf="certificate.remarks">
        <div class="col-12">
          <div class="form-group mb-3">
            <label class="form-label fw-bold">Remarks</label>
            <div class="form-control-plaintext border p-2 bg-light">
              {{ certificate.remarks }}
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>

</div>
